import React, { useState, useRef, useEffect } from "react";
import { BsChevronDown } from "react-icons/bs";
import { FaTimes, FaCheck, FaArrowLeft, FaEnvelope } from "react-icons/fa";
import { GetClinicalSitesByOrg } from "../../hooks/getClinicalSitesByOrg";
import { useClinicalLocations } from "../../hooks/getClinicalLocations";
import { useClinicalSiteRoles } from "../../hooks/getClinicalSiteRoles";
import {
  useRegisterBasicInfo,
  useRegisterLocation,
} from "../../hooks/createUser";
import useAllPositions from "../../hooks/useAllPositions";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

// The role the API attaches a position to — every other role ignores positionId
// (docs/two-step-registration.md §2).
const POSITION_ROLE = "HEALTH_CARE_PROVIDER";

// Roles come back as raw enum names (HEALTH_CARE_PROVIDER); show them as words.
const humanizeRole = (role) =>
  role
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const NewUser = () => {
  const modalRef = useRef(null);

  // Wizard: 1 = who is this person, 2 = where do they work / what role.
  const [step, setStep] = useState(1);

  // Step 1 fields
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [email, setEmail] = useState("");

  // The person step 1 created or found, carried into step 2.
  const [registeredUser, setRegisteredUser] = useState(null);
  const [userAlreadyExisted, setUserAlreadyExisted] = useState(false);
  // The exact details step 1 was last submitted with, so stepping back and
  // forward again without editing anything doesn't re-post them — the account
  // we just created would come back as "already exists" and say so.
  const [submittedBasicInfo, setSubmittedBasicInfo] = useState("");

  // Step 2 fields
  const [clinicalSite, setClinicalSite] = useState("");
  const [selectedClinicalSiteId, setSelectedClinicalSiteId] = useState(null);
  const [clinicalSiteIcon, setClinicalSiteIcon] = useState(false);
  const [location, setLocation] = useState("");
  const [locationIcon, setLocationIcon] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesIcon, setRolesIcon] = useState(false);
  const [position, setPosition] = useState("");
  const [positionIcon, setPositionIcon] = useState(false);

  const [formError, setFormError] = useState("");

  const { data: clinicalSites } = GetClinicalSitesByOrg();
  const { data: clinicalLocations, isLoading: locationsLoading } =
    useClinicalLocations(selectedClinicalSiteId);
  const { data: clinicalSiteRoles, isLoading: rolesLoading } =
    useClinicalSiteRoles();
  const { data: positions, isLoading: positionsLoading } = useAllPositions();

  const { mutate: registerBasicInfoMutation, isPending: isSavingBasicInfo } =
    useRegisterBasicInfo();
  const { mutate: registerLocationMutation, isPending: isAssigningLocation } =
    useRegisterLocation();

  const needsPosition = selectedRoles.includes(POSITION_ROLE);

  const resetWizard = () => {
    setStep(1);
    setLastName("");
    setFirstName("");
    setDateOfBirth("");
    setEmail("");
    setRegisteredUser(null);
    setUserAlreadyExisted(false);
    setSubmittedBasicInfo("");
    setClinicalSite("");
    setSelectedClinicalSiteId(null);
    setLocation("");
    setSelectedRoles([]);
    setPosition("");
    setClinicalSiteIcon(false);
    setLocationIcon(false);
    setRolesIcon(false);
    setPositionIcon(false);
    setFormError("");
  };

  // Start from a clean step 1 every time the modal is dismissed, so a cancelled
  // invite doesn't leave a half-filled form behind for the next one. Bootstrap
  // fires its modal events through jQuery, so we have to listen the same way.
  useEffect(() => {
    if (!modalRef.current || !window.$) return;
    const $modal = window.$(modalRef.current);
    $modal.on("hidden.bs.modal", resetWizard);
    return () => {
      $modal.off("hidden.bs.modal", resetWizard);
    };
  }, []);

  const closeModal = () => {
    if (modalRef.current) {
      window.$(modalRef.current).modal("hide");
    }
  };

  const handleClinicalSite = (site) => {
    setClinicalSite(site.name);
    setSelectedClinicalSiteId(site.id);
    setLocation(""); // Reset location when clinical site changes
    setClinicalSiteIcon(false);
  };

  const handleLocation = (selected) => {
    setLocation(selected);
    setLocationIcon(false);
  };

  const toggleRole = (role) => {
    setSelectedRoles((current) => {
      const next = current.includes(role)
        ? current.filter((r) => r !== role)
        : [...current, role];
      // Position only belongs to a health care provider — drop it if that role
      // is no longer selected.
      if (!next.includes(POSITION_ROLE)) {
        setPosition("");
      }
      return next;
    });
  };

  const handlePosition = (pos) => {
    setPosition(pos);
    setPositionIcon(false);
  };

  const getSelectedLocationName = () =>
    clinicalLocations?.data?.find((loc) => loc.id === location)?.name ||
    "Choose Location";

  const getSelectedRolesLabel = () => {
    if (selectedRoles.length === 0) return "Choose Role(s)";
    return selectedRoles.map(humanizeRole).join(", ");
  };

  const getSelectedPositionName = () => {
    if (!positions?.data || !position) return "Choose Position";
    return (
      positions.data.find((pos) => pos.id === position)?.name ||
      "Choose Position"
    );
  };

  // Step 1 → creates the person (or finds an existing account with this email)
  // and carries the returned id into step 2.
  const handleContinue = (e) => {
    e.preventDefault();
    setFormError("");

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setFormError("First name, last name and email are required.");
      return;
    }
    if (!isValidEmail(email)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    const basicInfo = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      ...(dateOfBirth ? { dateOfBirth } : {}),
    };

    // Nothing changed since the last submission — keep the user we already have.
    if (
      registeredUser?.id &&
      JSON.stringify(basicInfo) === submittedBasicInfo
    ) {
      setStep(2);
      return;
    }

    registerBasicInfoMutation(basicInfo, {
      onSuccess: ({ user, alreadyExists }) => {
        setRegisteredUser(user);
        setUserAlreadyExisted(alreadyExists);
        setSubmittedBasicInfo(JSON.stringify(basicInfo));
        setStep(2);
      },
      onError: (error) => {
        setFormError(error.message || "Couldn't save the user's details.");
      },
    });
  };

  // Step 2 → links the user to the location, assigns the role(s) and sends the
  // welcome email. Runs once: the modal closes on success.
  const handleCreateUser = (e) => {
    e.preventDefault();
    setFormError("");

    if (!registeredUser?.id) {
      setFormError("Something went wrong — please start again.");
      setStep(1);
      return;
    }
    if (!selectedClinicalSiteId || !location) {
      setFormError("Please choose a clinical site and a location.");
      return;
    }
    if (selectedRoles.length === 0) {
      setFormError("Please choose at least one role.");
      return;
    }
    if (needsPosition && !position) {
      setFormError("Please choose a position for the health care provider.");
      return;
    }

    registerLocationMutation(
      {
        userId: registeredUser.id,
        clinicalLocationId: location,
        roles: selectedRoles,
        ...(needsPosition && position ? { positionId: position } : {}),
        redirect: `${window.location.origin}/user/setpassword`,
      },
      {
        onSuccess: () => {
          toast.success(
            userAlreadyExisted
              ? "Role assigned and invitation email sent."
              : "User created and invitation email sent.",
          );
          closeModal();
        },
        onError: (error) => {
          setFormError(
            error.message || "Couldn't assign the location and role.",
          );
        },
      },
    );
  };

  const handleBack = () => {
    setFormError("");
    setStep(1);
  };

  return (
    <div
      ref={modalRef}
      className="modal fade"
      id="newUser"
      tabIndex="-1"
      aria-labelledby="newUser"
      aria-hidden="true"
    >
      <div className="modal-dialog" style={{ width: "395px" }}>
        <div className="modal-content border-0 overlay-rounded">
          <div className="modal-header mt-14 border-0 pb-0">
            <div className="flex-grow-1 text-center">
              <h5 className="text_primary mb-0" id="newUser">
                CREATE: New User
              </h5>
            </div>

            <FaTimes
              size={15}
              className="cursor-pointer text_primary"
              data-dismiss="modal"
              aria-label="Close"
            />
          </div>

          {/* Step indicator */}
          <div className="wizard-steps px-3 mt-3">
            <div className={`wizard-step ${step === 1 ? "active" : "done"}`}>
              <span className="wizard-step-dot">
                {step > 1 ? <FaCheck size={9} /> : 1}
              </span>
              <span className="wizard-step-label">User details</span>
            </div>
            <span
              className={`wizard-step-bar ${step > 1 ? "done" : ""}`}
              aria-hidden="true"
            />
            <div className={`wizard-step ${step === 2 ? "active" : ""}`}>
              <span className="wizard-step-dot">2</span>
              <span className="wizard-step-label">Role &amp; location</span>
            </div>
          </div>

          {step === 1 && (
            <form onSubmit={handleContinue}>
              <div className="modal-body px-3 pt-3">
                <p className="fs-13 text-muted mb-3">
                  Who is this person? We'll check whether they already have a
                  POMMe account.
                </p>

                <div className="form-group">
                  <input
                    type="text"
                    className="border-default rounded-sm overlay-dropdown-input small-text pl-12"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    className="border-default rounded-sm overlay-dropdown-input small-text pl-12"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="fs-13 text-muted mb-1">
                    Date of Birth (optional)
                  </label>
                  <input
                    type="date"
                    className="border-default rounded-sm overlay-dropdown-input small-text pl-12"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                  />
                </div>

                <div className="form-group mb-2">
                  <input
                    type="email"
                    className="border-default rounded-sm overlay-dropdown-input small-text pl-12"
                    placeholder="Enter New User's Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                {formError && (
                  <div className="wizard-alert wizard-alert-error fs-13">
                    {formError}
                  </div>
                )}
              </div>

              <div className="modal-footer d-flex justify-content-between align-items-center border-0">
                <button
                  type="button"
                  className="btn btn_outline_danger overlay-btn"
                  data-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-outline-primary overlay-btn"
                  disabled={isSavingBasicInfo}
                >
                  {isSavingBasicInfo ? "Checking..." : "Next: Role & Location"}
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleCreateUser}>
              <div className="modal-body px-3 pt-3">
                {/* Who step 1 landed on */}
                <div className="wizard-person mb-3">
                  <div className="small-text font-weight-bold">
                    {registeredUser?.firstName} {registeredUser?.lastName}
                  </div>
                  <div className="fs-13 text-muted">{email.trim()}</div>
                </div>

                {userAlreadyExisted && (
                  <div className="wizard-alert wizard-alert-info fs-13">
                    This email already has a POMMe account. Nothing about their
                    details changes — this step adds the role and location below
                    to that account.
                  </div>
                )}

                <div className="form-group mt-3">
                  <div className="dropdown">
                    <button
                      className="btn btn-block border text-left overlay-dropdown-input small-text"
                      type="button"
                      onClick={() => setClinicalSiteIcon(!clinicalSiteIcon)}
                    >
                      {clinicalSite || "Choose Clinical Site"}
                      <BsChevronDown
                        size={12}
                        style={{
                          transform: clinicalSiteIcon
                            ? "rotate(180deg)"
                            : "rotate(0)",
                          transition: "transform 0.3s ease",
                          float: "right",
                          marginTop: "3px",
                        }}
                      />
                    </button>
                    <div
                      className={`dropdown-menu w-100 ${
                        clinicalSiteIcon ? "show" : ""
                      }`}
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                        Choose one site only:
                      </p>
                      {clinicalSites?.data?.map((site) => (
                        <button
                          key={site.id}
                          type="button"
                          className="dropdown-item small-text"
                          onClick={() => handleClinicalSite(site)}
                        >
                          {site.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="dropdown">
                    <button
                      className="btn btn-block border text-left overlay-dropdown-input small-text"
                      type="button"
                      onClick={() => setLocationIcon(!locationIcon)}
                    >
                      {getSelectedLocationName()}
                      <BsChevronDown
                        size={12}
                        style={{
                          transform: locationIcon
                            ? "rotate(180deg)"
                            : "rotate(0)",
                          transition: "transform 0.3s ease",
                          float: "right",
                          marginTop: "3px",
                        }}
                      />
                    </button>
                    <div
                      className={`dropdown-menu w-100 ${
                        locationIcon ? "show" : ""
                      }`}
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                        Choose one location only:
                      </p>
                      {locationsLoading ? (
                        <div className="dropdown-item small-text text-muted">
                          Loading locations...
                        </div>
                      ) : selectedClinicalSiteId ? (
                        clinicalLocations?.data?.length > 0 ? (
                          clinicalLocations.data.map((loc) => (
                            <button
                              key={loc.id}
                              type="button"
                              className="dropdown-item small-text"
                              onClick={() => handleLocation(loc.id)}
                            >
                              {loc.name}
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item small-text text-muted">
                            No locations available
                          </div>
                        )
                      ) : (
                        <div className="dropdown-item small-text text-muted">
                          Please select a clinical site first
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Roles — the API takes several at one location */}
                <div className="form-group mb-2">
                  <div className="dropdown">
                    <button
                      className="btn btn-block border text-left overlay-dropdown-input small-text text-truncate"
                      type="button"
                      onClick={() => setRolesIcon(!rolesIcon)}
                    >
                      {getSelectedRolesLabel()}
                      <BsChevronDown
                        size={12}
                        style={{
                          transform: rolesIcon ? "rotate(180deg)" : "rotate(0)",
                          transition: "transform 0.3s ease",
                          float: "right",
                          marginTop: "3px",
                        }}
                      />
                    </button>
                    <div
                      className={`dropdown-menu w-100 ${rolesIcon ? "show" : ""}`}
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                        Choose one or more roles:
                      </p>
                      {rolesLoading ? (
                        <div className="dropdown-item small-text text-muted">
                          Loading roles...
                        </div>
                      ) : clinicalSiteRoles?.data?.length > 0 ? (
                        clinicalSiteRoles.data.map((role) => (
                          <label
                            key={role}
                            className="dropdown-item small-text d-flex align-items-center mb-0 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={selectedRoles.includes(role)}
                              onChange={() => toggleRole(role)}
                            />
                            {humanizeRole(role)}
                          </label>
                        ))
                      ) : (
                        <div className="dropdown-item small-text text-muted">
                          No roles available
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {selectedRoles.length > 0 && (
                  <div className="d-flex flex-wrap mb-3">
                    {selectedRoles.map((role) => (
                      <span key={role} className="wizard-chip fs-13">
                        {humanizeRole(role)}
                        <FaTimes
                          size={9}
                          className="ml-2 cursor-pointer"
                          onClick={() => toggleRole(role)}
                        />
                      </span>
                    ))}
                  </div>
                )}

                {/* Position only applies to a health care provider */}
                {needsPosition && (
                  <div className="form-group">
                    <div className="dropdown">
                      <button
                        className="btn btn-block border text-left overlay-dropdown-input small-text"
                        type="button"
                        onClick={() => setPositionIcon(!positionIcon)}
                      >
                        {getSelectedPositionName()}
                        <BsChevronDown
                          size={12}
                          style={{
                            transform: positionIcon
                              ? "rotate(180deg)"
                              : "rotate(0)",
                            transition: "transform 0.3s ease",
                            float: "right",
                            marginTop: "3px",
                          }}
                        />
                      </button>
                      <div
                        className={`dropdown-menu w-100 ${
                          positionIcon ? "show" : ""
                        }`}
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                          Choose one position only:
                        </p>
                        {positionsLoading ? (
                          <div className="dropdown-item small-text text-muted">
                            Loading positions...
                          </div>
                        ) : positions?.data?.length > 0 ? (
                          positions.data.map((pos) => (
                            <button
                              key={pos.id}
                              type="button"
                              className="dropdown-item small-text"
                              onClick={() => handlePosition(pos.id)}
                            >
                              {pos.name}
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item small-text text-muted">
                            No positions available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="wizard-note fs-13 d-flex align-items-start">
                  <FaEnvelope size={11} className="mr-2 mt-1 flex-shrink-0" />
                  <span>
                    Finishing sends {registeredUser?.firstName || "the user"} an
                    invitation email with a link to set their password.
                  </span>
                </div>

                {formError && (
                  <div className="wizard-alert wizard-alert-error fs-13 mt-2">
                    {formError}
                  </div>
                )}
              </div>

              <div className="modal-footer d-flex justify-content-between align-items-center border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary overlay-btn d-flex align-items-center justify-content-center"
                  onClick={handleBack}
                  disabled={isAssigningLocation}
                >
                  <FaArrowLeft size={11} className="mr-2" />
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-outline-primary overlay-btn"
                  disabled={isAssigningLocation}
                >
                  {isAssigningLocation ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewUser;
